import {EmailCaptureApiCaller} from "../../../EmailCaptureApiCaller";

export class DesktopModal {
    private readonly modal: HTMLElement | null;
    private readonly aside: HTMLElement | null;
    private readonly form: HTMLFormElement | null;
    private readonly errorElement: HTMLElement | null;
    private readonly emailInput: HTMLInputElement | null;
    private readonly successContainer: HTMLElement | null;
    private readonly title: HTMLElement | null;
    private readonly description: HTMLElement | null;
    private readonly nearbyGeosContainer: HTMLElement | null;
    private readonly nearbyGeoElements: NodeListOf<HTMLElement> | null;

    private removedNearbyGeos: Set<string> = new Set();
    private onSuccessCallback: (() => void) | null = null;

    constructor(private readonly apiCaller: EmailCaptureApiCaller) {
        this.modal = document.querySelector('.js-serp-email-capture-modal');
        this.aside = document.querySelector('.js-serp-email-capture-aside');
        this.form = document.querySelector('.js-serp-email-capture-form');
        this.errorElement = document.querySelector('.js-serp-email-capture-email-feedback');
        this.emailInput = this.modal?.querySelector('.js-serp-email-capture-email-input') as HTMLInputElement | null;
        this.successContainer = this.modal?.querySelector('.js-serp-email-capture-success') as HTMLElement | null;
        this.title = this.modal?.querySelector('.serp-email-capture-modal__title') as HTMLElement | null;
        this.description = this.modal?.querySelector('.serp-email-capture-modal__description') as HTMLElement | null;
        this.nearbyGeosContainer = this.modal?.querySelector('.js-nearby-geos-container') as HTMLElement | null;
        this.nearbyGeoElements = this.modal?.querySelectorAll('.js-nearby-geo') as NodeListOf<HTMLElement> | null;
    }

    init(): void {
        if (!this.modal || !this.aside) return;

        this.prefillEmail();
        this.registerAsideClick();
        this.registerCloseEvents();
        this.registerFormSubmit();
        this.registerNearbyGeoRemove();
    }

    isAvailable(): boolean {
        return this.modal !== null && this.aside !== null;
    }

    onSuccess(callback: () => void): void {
        this.onSuccessCallback = callback;
    }

    show(): void {
        if (!this.modal) return;

        this.modal.classList.add('displayed');
        document.body.style.overflow = 'hidden';
        this.hideSuccess();
        this.prefillEmail();
        this.resetNearbyGeos();

        if (this.emailInput) {
            setTimeout(() => this.emailInput?.focus(), 100);
        }
    }

    hide(): void {
        if (!this.modal) return;

        this.modal.classList.remove('displayed');
        document.body.style.overflow = '';
        this.hideError();
        this.hideSuccess();

        if (this.emailInput) {
            this.emailInput.value = '';
        }

        const submitButton = this.form?.querySelector('.js-serp-email-capture-submit') as HTMLButtonElement;
        if (submitButton) submitButton.disabled = false;
    }

    private prefillEmail(): void {
        const savedEmail = this.apiCaller.getEmailFromStorage();
        if (savedEmail && this.emailInput) {
            this.emailInput.value = savedEmail;
        }
    }

    private registerAsideClick(): void {
        this.aside?.addEventListener('click', () => {
            this.show();
        });
    }

    private registerCloseEvents(): void {
        const closeButtons = document.querySelectorAll('.js-close-serp-email-capture-modal');
        closeButtons.forEach((btn) => {
            btn.addEventListener('click', () => this.hide());
        });

        document.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Escape' && this.isVisible()) {
                this.hide();
            }
        });
    }

    private registerNearbyGeoRemove(): void {
        this.nearbyGeoElements?.forEach(geo => {
            const removeBtn = geo.querySelector('.js-nearby-geo-remove');
            removeBtn?.addEventListener('click', () => {
                const location = geo.dataset.location;
                if (location) {
                    this.removedNearbyGeos.add(location);
                    geo.classList.add('serp-email-capture-modal__nearby-geo--removed');
                }
            });
        });
    }

    private resetNearbyGeos(): void {
        this.removedNearbyGeos.clear();
        this.nearbyGeoElements?.forEach(geo => {
            geo.classList.remove('serp-email-capture-modal__nearby-geo--removed');
        });
    }

    private getSelectedNearbyGeoLocations(): string[] {
        const selected: string[] = [];
        this.nearbyGeoElements?.forEach(geo => {
            const location = geo.dataset.location;
            if (location && !this.removedNearbyGeos.has(location)) {
                selected.push(location);
            }
        });
        return selected;
    }

    private registerFormSubmit(): void {
        if (!this.form) return;

        this.form.addEventListener('submit', async (e: Event) => {
            e.preventDefault();

            const email = this.emailInput?.value?.trim();

            if (!email || !this.apiCaller.isValidEmail(email)) {
                this.showError();
                return;
            }

            this.hideError();

            const submitButton = this.form!.querySelector('.js-serp-email-capture-submit') as HTMLButtonElement;
            submitButton.disabled = true;

            try {
                const alertParams = this.apiCaller.getAlertParamsFromContainer(this.modal, this.form);
                const nearbyGeoLocations = this.getSelectedNearbyGeoLocations();
                await this.apiCaller.submitAlerts(email, nearbyGeoLocations, alertParams);
                this.apiCaller.saveEmailToStorage(email);
                this.showSuccess();
            } catch (error) {
                this.showError();
                submitButton.disabled = false;
            }
        });
    }

    private isVisible(): boolean {
        return this.modal?.classList.contains('displayed') || false;
    }

    private showSuccess(): void {
        if (this.form) this.form.style.display = 'none';
        if (this.title) this.title.style.display = 'none';
        if (this.description) this.description.style.display = 'none';
        if (this.nearbyGeosContainer) this.nearbyGeosContainer.style.display = 'none';
        if (this.successContainer) this.successContainer.classList.add('visible');

        this.onSuccessCallback?.();

        setTimeout(() => this.hide(), 3000);
    }

    private hideSuccess(): void {
        if (this.form) this.form.style.display = '';
        if (this.title) this.title.style.display = '';
        if (this.description) this.description.style.display = '';
        if (this.nearbyGeosContainer) this.nearbyGeosContainer.style.display = '';
        if (this.successContainer) this.successContainer.classList.remove('visible');
    }

    private showError(): void {
        this.apiCaller.toggleError(this.errorElement, this.emailInput, true);
    }

    private hideError(): void {
        this.apiCaller.toggleError(this.errorElement, this.emailInput, false);
    }
}
